// frontend/src/pages/employee/Shop.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Shop.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

function Shop({ user }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState({ items: [], total: 0, order_id: null });
  const [orders, setOrders] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('catalog');
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  const checkAccess = useCallback(async () => {
    setCheckingAccess(true);
    try {
      const response = await axios.get(`${API_URL}/api/shop/access`, {
        params: { employee_id: user.id }
      });
      setHasAccess(response.data.has_access);
      if (response.data.has_access) {
        fetchProducts();
        fetchCart();
        fetchOrders();
      }
    } catch (err) {
      setError('Ошибка проверки доступа');
    } finally {
      setCheckingAccess(false);
    }
  }, [user.id]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { company_id: user.company_id, employee_id: user.id };
      if (selectedCategory) params.category_id = selectedCategory;
      
      const response = await axios.get(`${API_URL}/api/shop/products`, { params });
      setProducts(response.data);
      
      const cats = {};
      response.data.forEach(p => {
        if (p.category_id && !cats[p.category_id]) {
          cats[p.category_id] = { id: p.category_id, name: p.category_name };
        }
      });
      setCategories(Object.values(cats));
    } catch (err) {
      setError('Ошибка загрузки товаров');
    } finally {
      setLoading(false);
    }
  }, [user.company_id, user.id, selectedCategory]);

  const fetchCart = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/shop/cart`, {
        params: { employee_id: user.id }
      });
      setCart(response.data);
    } catch (err) {
      console.error('Ошибка загрузки корзины:', err);
    }
  }, [user.id]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/shop/orders`, {
        params: { employee_id: user.id }
      });
      setOrders(response.data);
    } catch (err) {
      console.error('Ошибка загрузки заказов:', err);
    }
  }, [user.id]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  const addToCart = async (productId, quantity = 1) => {
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('employee_id', user.id);
      formData.append('company_id', user.company_id);
      formData.append('product_id', productId);
      formData.append('quantity', quantity);
      
      await axios.post(`${API_URL}/api/shop/cart/add`, formData);
      setSuccess('✅ Товар добавлен в корзину');
      await fetchCart();
      await fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка добавления');
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    setError('');
    setLoading(true);
    
    try {
      await axios.delete(`${API_URL}/api/shop/cart/items/${itemId}`, {
        params: { employee_id: user.id }
      });
      await fetchCart();
      await fetchProducts();
    } catch (err) {
      setError('Ошибка удаления');
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async () => {
    if (cart.items.length === 0) {
      setError('Корзина пуста');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('employee_id', user.id);
      
      const response = await axios.post(`${API_URL}/api/shop/order/create`, formData);
      setSuccess(`✅ Заказ оформлен! Номер: ${response.data.order_number}`);
      await fetchCart();
      await fetchOrders();
      setActiveTab('history');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка оформления заказа');
    } finally {
      setLoading(false);
    }
  };

  const uploadReceipt = async (orderId, file) => {
    setError('');
    setLoading(true);
    
    const formData = new FormData();
    formData.append('employee_id', user.id);
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/shop/order/${orderId}/receipt`,
        formData
      );
      setSuccess('✅ Чек загружен! Ожидайте подтверждения.');
      await fetchOrders();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки чека');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'cart': '🟡 В корзине',
      'awaiting_payment': '🟣 Ожидает оплаты',
      'awaiting_verification': '🟠 На проверке',
      'paid': '🟢 Оплачен',
      'delivered': '🔵 Выдан',
      'cancelled': '🔴 Отменён'
    };
    return labels[status] || status;
  };

  if (checkingAccess) {
    return <div className="shop-loading">Проверка доступа...</div>;
  }

  if (!hasAccess) {
    return (
      <div className="shop-no-access">
        <h3>🛍️ Магазин</h3>
        <div className="card">
          <p>🚫 Доступ к магазину закрыт</p>
          <p className="text-light">Обратитесь к администратору для получения доступа</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-employee">
      <div className="shop-header">
        <h3>🛍️ Магазин</h3>
        <div className="shop-header-right">
          <button 
            className={`btn btn-sm ${activeTab === 'catalog' ? '' : 'btn-outline'}`}
            onClick={() => setActiveTab('catalog')}
          >
            📦 Каталог
          </button>
          <button 
            className={`btn btn-sm ${activeTab === 'cart' ? '' : 'btn-outline'}`}
            onClick={() => setActiveTab('cart')}
          >
            🛒 Корзина {cart.items.length > 0 && <span className="cart-badge">{cart.items.length}</span>}
          </button>
          <button 
            className={`btn btn-sm ${activeTab === 'history' ? '' : 'btn-outline'}`}
            onClick={() => setActiveTab('history')}
          >
            📜 История
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {activeTab === 'catalog' && (
        <CatalogTab 
          products={products}
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          onAdd={addToCart}
          loading={loading}
        />
      )}

      {activeTab === 'cart' && (
        <CartTab 
          cart={cart}
          onRemove={removeFromCart}
          onCreateOrder={createOrder}
          loading={loading}
        />
      )}

      {activeTab === 'history' && (
        <HistoryTab 
          orders={orders}
          onUploadReceipt={uploadReceipt}
          loading={loading}
          getStatusLabel={getStatusLabel}
        />
      )}
    </div>
  );
}

// ============================================================
// КАТАЛОГ
// ============================================================

function CatalogTab({ products, categories, selectedCategory, setSelectedCategory, onAdd, loading }) {
  return (
    <div className="shop-catalog">
      <div className="shop-catalog-filters">
        <button 
          className={`btn btn-sm ${!selectedCategory ? '' : 'btn-outline'}`}
          onClick={() => setSelectedCategory('')}
        >
          Все
        </button>
        {categories.map(c => (
          <button 
            key={c.id}
            className={`btn btn-sm ${selectedCategory === c.id ? '' : 'btn-outline'}`}
            onClick={() => setSelectedCategory(c.id)}
          >
            {c.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-spinner"></div>
      ) : products.length === 0 ? (
        <div className="empty-state card">
          <p>📭 Товаров пока нет</p>
          <p className="text-light">Загрузка товаров ожидается</p>
        </div>
      ) : (
        <div className="shop-products-grid">
          {products.map(p => (
            <div key={p.id} className="shop-product-card card">
              <div className="shop-product-info">
                <div className="shop-product-name">{p.name}</div>
                <div className="shop-product-meta">
                  <span className="shop-product-price">{p.price} ₽</span>
                  <span className="shop-product-unit">за {p.unit}</span>
                </div>
                {p.description && (
                  <div className="shop-product-desc">{p.description}</div>
                )}
              </div>
              <button 
                className="btn btn-sm add-to-cart-btn"
                onClick={() => onAdd(p.id, 1)}
                disabled={loading}
              >
                ➕ В корзину
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// КОРЗИНА
// ============================================================

function CartTab({ cart, onRemove, onCreateOrder, loading }) {
  return (
    <div className="shop-cart">
      {cart.items.length === 0 ? (
        <div className="empty-state card">
          <p>🛒 Корзина пуста</p>
          <p className="text-light">Добавьте товары из каталога</p>
        </div>
      ) : (
        <>
          <div className="shop-cart-items">
            {cart.items.map(item => (
              <div key={item.id} className="shop-cart-item card">
                <div className="shop-cart-item-info">
                  <div className="shop-cart-item-name">{item.product_name}</div>
                  <div className="shop-cart-item-meta">
                    <span>{item.quantity} × {item.price} ₽ = <strong>{item.total} ₽</strong></span>
                    <span className="shop-cart-item-unit">{item.unit}</span>
                  </div>
                </div>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => onRemove(item.id)}
                  disabled={loading}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
          <div className="shop-cart-total card">
            <div className="shop-cart-total-row">
              <span>Итого:</span>
              <span className="shop-cart-total-amount">{cart.total} ₽</span>
            </div>
            <button 
              className="btn"
              onClick={onCreateOrder}
              disabled={loading || cart.items.length === 0}
            >
              📦 Оформить заказ
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// ИСТОРИЯ ЗАКАЗОВ
// ============================================================

function HistoryTab({ orders, onUploadReceipt, loading, getStatusLabel }) {
  const handleFileUpload = (orderId, e) => {
    const file = e.target.files[0];
    if (file) {
      onUploadReceipt(orderId, file);
      e.target.value = '';
    }
  };

  return (
    <div className="shop-history">
      {orders.length === 0 ? (
        <div className="empty-state card">
          <p>📜 Нет заказов</p>
          <p className="text-light">Сделайте первый заказ</p>
        </div>
      ) : (
        orders.map(order => (
          <div key={order.id} className="shop-order-item card">
            <div className="shop-order-header">
              <div className="shop-order-number">
                <strong>№{order.order_number}</strong>
                <span className="shop-order-date">{new Date(order.created_at).toLocaleDateString('ru-RU')}</span>
              </div>
              <div className="shop-order-status">
                {getStatusLabel(order.status)}
              </div>
            </div>
            
            <div className="shop-order-items">
              {order.items.map((item, idx) => (
                <div key={idx} className="shop-order-item-detail">
                  <span>{item.product_name}</span>
                  <span>{item.quantity} × {item.price} ₽ = {item.total} ₽</span>
                </div>
              ))}
            </div>
            
            <div className="shop-order-footer">
              <div className="shop-order-total">
                <strong>Итого: {order.total_amount} ₽</strong>
              </div>
              
              {order.status === 'awaiting_payment' && (
                <div className="shop-order-receipt-upload">
                  <input 
                    type="file" 
                    accept=".jpg,.jpeg,.png,.gif,.pdf"
                    onChange={(e) => handleFileUpload(order.id, e)}
                    id={`receipt-${order.id}`}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor={`receipt-${order.id}`} className="btn btn-sm">
                    📎 Прикрепить чек
                  </label>
                </div>
              )}
              
              {order.receipt_files && order.receipt_files.length > 0 && (
                <div className="shop-order-receipts">
                  <span>📎 Чек приложен ({order.receipt_files.length} файлов)</span>
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default Shop;
